import React, { useEffect, useState } from 'react';
import {
  Typography,
  Container,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar, // We'll style this to be rectangular or use a Box
  ListItemText,
  Divider,
  Chip,
  Paper,
  Stack,
  createTheme,
  ThemeProvider,
  CssBaseline,
  ListItemIcon,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import { get, post, put, del } from '../utility/fetch';
import { get as gets } from '../utility/fetchWeb';
import notification from '../utility/notification';
import { useNavigate } from 'react-router-dom';

// Custom MUI theme for consistent styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#28a745', // Green for accents
    },
    secondary: {
      main: '#f8f9fa', // Light background for the page
    },
    background: {
      default: '#f8f9fa',
      paper: '#fff', // White background for the group list Paper
    },
    text: {
      primary: '#333',
      secondary: '#6c757d',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.8rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    subtitle1: { // For group names
      fontWeight: 500,
      fontSize: '1.05rem',
    },
    body2: { // For members and new posts count
      fontSize: '0.9rem',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Rounded corners for the main group list card
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '25px', // Rounded search bar
            backgroundColor: '#f5f5f5', // Light background
            '& fieldset': {
              borderColor: 'transparent', // No border by default
            },
            '&:hover fieldset': {
              borderColor: '#e0e0e0', // Subtle border on hover
            },
            '&.Mui-focused fieldset': {
              borderColor: '#28a745', // Green border on focus
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#6c757d', // Default icon button color
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#eafcec', // Light green background for chips
          color: "green", // Green text
          borderRadius: '8px', // Rounded corners for chips
          fontWeight: 500,
          padding: '4px 8px', // Adjust padding
          // Style for the delete icon
          '& .MuiChip-deleteIcon': {
            color: "green", // Green 'x' icon
            fontSize: '1rem',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 600, // Make names bolder
          lineHeight: 1.2,
        },
        secondary: {
          color: "grey", // Grey for members and posts
        }
      }
    },
    // Custom style for the rectangular group images within ListItemAvatar
    MuiListItemAvatar: {
      styleOverrides: {
        root: {
          minWidth: 'unset', // Remove default min-width to control spacing better
          marginRight: '16px', // Space between image and text
        },
      },
    },
    MuiAvatar: { // Styling if we decide to use Avatar component but make it square
      styleOverrides: {
        root: {
          borderRadius: '8px', // Make it square with slight rounded corners
          width: 60, // Fixed width
          height: 60, // Fixed height
        }
      }
    }
  },
});

function GroupCreated({ selectedGroup, setSelectedGroup }) {
  const navigate = useNavigate();
  const [filters, setFilters] = React.useState(['Group Size', 'Number of Post', 'Ratings']);
  const [groups, setGroups] = React.useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [sentInvites, setSentInvites] = useState([]);
  const [receivedInvites, setReceivedInvites] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [users, setUsers] = useState([])
  const [employees, setEmployees] = useState([]);
  const [patients, setPatients] = useState([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedGroupForInvite, setSelectedGroupForInvite] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const getGroupJoined = async () => {
    try {
      let res = await get("/communitygroup/list");
      if (res.statusCode === 200) {
        // Get current user ID
        const currentUserId = Number(sessionStorage.getItem("userId"));

        // Filter groups where current user is the creator
        const filteredGroups = res.data.filter(group =>

          group?.communityGroupAdmin?.employee?.id === currentUserId
        );

        console.log('Created Groups:', filteredGroups, currentUserId); // For debugging
        setGroups(filteredGroups);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const getPatients = async () => {
    try {
      const res = await gets('/patient');
      if (res?.data) {
        setPatients(res.data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      notification({
        type: 'error',
        message: 'Failed to fetch patients',
      });
    }
  };

  const getEmployees = async () => {
    try {
      const res = await get('/employee/list/1/10000');
      if (res?.statusCode === 200) {
        setEmployees(res?.data?.recordList);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      setEmployees([]);
      console.error('Error fetching employees:', error);
    }
  };

  const getSentInvites = async () => {
    try {
      const userId = sessionStorage.getItem("userId");
      const response = await get(`/communitygroupinvite/list/employee/${userId}`);
      if (response?.statusCode === 200) {
        setSentInvites(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching sent invites:', error);
      setSentInvites([]);
    }
  };

  const getReceivedInvites = async () => {
    try {
      const userId = sessionStorage.getItem("userId");
      const response = await get(`/communitygroupinvite/list/receiver-employee/${userId}`);
      if (response?.statusCode === 200) {
        setReceivedInvites(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching received invites:', error);
      setReceivedInvites([]);
    }
  };

  const handleDeleteFilter = (chipToDelete) => () => {
    setFilters((chips) => chips.filter((chip) => chip !== chipToDelete));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSendInvite = async (groupId, receiverId, isHealthPractitioner) => {
    try {
      const payload = {
        communityGroupId: groupId,
        communityGroupInvites: [{
          receiverPatientId: isHealthPractitioner ? 0 : receiverId,
          receiverEmployeeId: isHealthPractitioner ? receiverId : 0,
          isHealthPractitioner
        }]
      };

      const response = await post('/communitygroupinvite', payload);
      if (response?.statusCode === 200) {
        notification({
          type: 'success',
          message: 'Invite sent successfully',
          duration: 3000
        });
        // Refresh sent invites
        getSentInvites();
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      notification({
        type: 'error',
        message: 'Failed to send invite',
        duration: 3000
      });
    }
  };

  const handleRespondToInvite = async (inviteId, isAccepted) => {
    try {
      const response = await put(`/communitygroupinvite/${inviteId}/respond-to-invite`, {
        isAccepted
      });
      if (response?.statusCode === 200) {
        notification({
          type: 'success',
          message: isAccepted ? 'Invite accepted' : 'Invite declined',
          duration: 3000
        });
        // Refresh received invites
        getReceivedInvites();
      }
    } catch (error) {
      console.error('Error responding to invite:', error);
      notification({
        type: 'error',
        message: 'Failed to respond to invite',
        duration: 3000
      });
    }
  };

  // Add the delete handler function after other handlers
  const handleDeleteInvite = async (inviteId) => {
    try {
      const response = await del(`/communitygroupinvite/${inviteId}`);
      if (response?.statusCode === 200) {
        notification({
          type: 'success',
          message: 'Invite deleted successfully',
          duration: 3000
        });
        // Refresh sent invites list
        getSentInvites();
      }
    } catch (error) {
      console.error('Error deleting invite:', error);
      notification({
        type: 'error',
        message: 'Failed to delete invite',
        duration: 3000
      });
    }
  };

  // Add this handler function inside the GroupCreated component
  const handleDeleteGroup = async (groupId) => {
    try {
      const response = await del(`/communitygroup/${groupId}`);
      if (response?.message === "CommunityGroup Deleted Successfully") {
        notification({
          type: 'success',
          message: 'Group deleted successfully',
          duration: 3000
        });
        // Refresh groups list
        getGroupJoined();
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      notification({
        type: 'error',
        message: 'Failed to delete group',
        duration: 3000
      });
    }
  };

  const handleDeleteClick = (e, group) => {
    e.stopPropagation();
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (groupToDelete) {
      await handleDeleteGroup(groupToDelete.id);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  // Update the edit handler in the IconButton
  const handleEditClick = (e, group) => {
    e.stopPropagation();
    navigate('/create-group', {
      state: {
        isEditing: true,
        groupData: {
          id: group?.id,
          name: group?.name,
          categoryId: group?.categoryId,
          description: group?.description,
          pictureUrl: group?.pictureUrl
        }
      }
    });
  };

  useEffect(() => {
    switch (activeTab) {
      case 0:
        getGroupJoined();
        break;
      case 1:
        getSentInvites();
        break;
      case 2:
        getReceivedInvites();
        break;
      default:
        break;
    }
  }, [activeTab]);

  useEffect(() => {
    getPatients();
    getEmployees();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: theme.palette.background.default, width: "100%", minHeight: '100vh', py: 4 }}>
        <Container sx={{ width: "80%" }}> {/* Adjusted maxWidth for the list layout */}
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, ml: { xs: 0, sm: 2 } }}>
            <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 0 }}>
              Group Created
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-group')}
            >
              Create Group
            </Button>
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ mb: 3 }}
          >
            <Tab label="Created Groups" />
            <Tab label="Sent Invites" />
            <Tab label="Received Invites" />
          </Tabs>

          {/* Search and Filter */}
          {activeTab === 0 && (
            <>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 3, px: { xs: 0, sm: 2 } }}
              >
                <TextField
                  variant="outlined"
                  placeholder="Search for Group"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton aria-label="filter">
                  <FilterListIcon />
                </IconButton>
              </Stack>

              {/* Filter Chips */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 3,
                  px: { xs: 0, sm: 2 },
                  flexWrap: 'wrap', // Allow chips to wrap to the next line
                  '& > *': { mb: { xs: 1, sm: 0 } }, // Add bottom margin for wrapped chips
                }}
              >
                {filters.map((data) => (
                  <Chip
                    key={data}
                    label={data}
                    onDelete={handleDeleteFilter(data)}
                  />
                ))}
              </Stack>
            </>
          )}

          {/* Group List */}
          {activeTab === 0 && (
            <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto' }}>
              <List disablePadding>
                {groups?.length === 0 ? (
                  <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No Groups Created Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      You haven't created any groups yet. Start creating your first group!
                    </Typography>
                  </Box>
                ) : (
                  groups?.map((group, index) => (
                    <React.Fragment key={group?.id}>
                      <ListItem
                        sx={{
                          py: 1.5,
                          px: { xs: 2, sm: 3 },
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            cursor: 'pointer',
                            '& .action-buttons': {
                              opacity: 1,
                            }
                          },
                        }}
                        onClick={() => setSelectedGroup(group)}
                      >
                        <ListItemAvatar>
                          {/* Using Box for the image to maintain rectangular shape */}
                          <Box
                            sx={{
                              width: 60, // Fixed width
                              height: 60, // Fixed height
                              borderRadius: '8px', // Rounded corners for the image
                              backgroundImage: `url("${encodeURI(group?.pictureUrl)}")`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              flexShrink: 0, // Prevent image from shrinking
                            }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" noWrap>
                              {group?.name}
                            </Typography>
                          }
                          secondary={
                            <React.Fragment>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {group?.communityGroupMembers?.length + " members"} {/* Assuming this is the members count */}
                              </Typography>
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }} noWrap>
                                {group?.newPosts}
                              </Typography>
                            </React.Fragment>
                          }
                          sx={{ ml: 1 }} // Margin left for text next to image
                          onClick={() => setSelectedGroup(group)} // Handle group selection
                        />
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          className="action-buttons"
                          sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s ease'
                          }}
                        >
                          <IconButton
                            onClick={(e) => handleEditClick(e, group)}
                            size="small"
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.lighter',
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={(e) => handleDeleteClick(e, group)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.lighter',
                              }
                            }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedGroupForInvite(group);
                              setInviteModalOpen(true);
                            }}
                            size="small"
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.lighter',
                              }
                            }}
                          >
                            <PeopleIcon />
                          </IconButton>
                          <ChevronRightIcon color="action" />
                        </Stack>
                      </ListItem>
                      {index < groups?.length - 1 && (
                        <Divider
                          component="li"
                          sx={{ ml: { xs: '80px', sm: '92px' }, mr: 2 }}
                        />
                      )}
                    </React.Fragment>
                  ))
                )}
              </List>
            </Paper>
          )}

          {/* Sent Invites */}
          {activeTab === 1 && (
            <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto' }}>
              <List disablePadding>
                {sentInvites?.length === 0 ? (
                  <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No Invites Sent
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      You haven't sent any group invites yet
                    </Typography>
                  </Box>
                ) : (
                  sentInvites?.map((invite, index) => {
                    // Determine receiver details
                    const receiver = invite.employeeReceiver?.firstName && invite.employeeReceiver?.lastName ? invite.employeeReceiver : invite.patientReceiver;
                    const receiverName = receiver ? 
                      `${receiver.firstName} ${receiver.lastName}` : 
                      'Unknown Receiver';
                    const receiverPicture = receiver?.profilePicture || receiver?.pictureUrl;
                    const receiverType = invite.employeeReceiver?.firstName && invite.employeeReceiver?.lastName ? 'Doctor' : 'Citizen';

                    return (
                      <React.Fragment key={invite.id}>
                        <ListItem
                          sx={{
                            py: 1.5,
                            px: { xs: 2, sm: 3 },
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            },
                          }}
                          secondaryAction={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={invite.status}
                                size="small"
                                color={invite.status === 'Pending' ? 'warning' : 'success'}
                                variant="outlined"
                              />
                              {invite.status === 'Pending' && (
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => handleDeleteInvite(invite.id)}
                                  color="error"
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar 
                              src={receiverPicture} 
                              sx={{ 
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText' 
                              }}
                            >
                              {receiverName.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2">
                                {receiverName}
                              </Typography>
                            }
                            secondary={
                              <Stack direction="column" spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  {receiverType}
                                </Typography>
                                <Typography variant="caption" color="text.primary">
                                  Invited to: {invite.communityGroup?.name}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                        {index < sentInvites.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </List>
            </Paper>
          )}

          {/* Received Invites */}
          {activeTab === 2 && (
            <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto' }}>
              <List disablePadding>
                {receivedInvites?.length === 0 ? (
                  <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No Invites Received
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      You don't have any pending group invitations
                    </Typography>
                  </Box>
                ) : (
                  receivedInvites?.map((invite, index) => (
                    <React.Fragment key={invite.id}>
                      <ListItem
                        secondaryAction={
                          invite.status === 'pending' && (
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleRespondToInvite(invite.id, true)}
                              >
                                Accept
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleRespondToInvite(invite.id, false)}
                              >
                                Decline
                              </Button>
                            </Stack>
                          )
                        }
                      >
                        <ListItemAvatar>
                          <Avatar src={invite.senderPictureUrl} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={invite.senderName}
                          secondary={`Invited you to join ${invite.groupName}`}
                        />
                      </ListItem>
                      {index < receivedInvites?.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </List>
            </Paper>
          )}
        </Container>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Group?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{groupToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inviteModalOpen}
        onClose={() => {
          setInviteModalOpen(false);
          setSelectedUsers([]);
          setSearchTerm('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Invite Members to {selectedGroupForInvite?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {[...patients, ...employees]
              ?.filter(user => 
                `${user?.firstName} ${user?.lastName}`
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
              )
              .map((user) => (
                <ListItem
                  key={`${user.id}-${user.userRoles ? 'employee' : 'Citizen'}`}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      onChange={() => {
                        setSelectedUsers(prev => {
                          const existing = prev.find(u => u.id === user.id);
                          if (existing) {
                            return prev.filter(u => u.id !== user.id);
                          }
                          return [...prev, { 
                            id: user.id, 
                            isHealthPractitioner: Boolean(user.userRoles)
                          }];
                        });
                      }}
                      checked={selectedUsers.some(u => u.id === user.id)}
                    />
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={user.pictureUrl || user.profilePicture}>
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${user.firstName} ${user.lastName}`}
                    secondary={user.userRoles ? 'Doctor' : 'Citizen'}
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setInviteModalOpen(false);
            setSelectedUsers([]);
            setSearchTerm('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (selectedGroupForInvite && selectedUsers.length > 0) {
                try {
                  const payload = {
                    communityGroupId: selectedGroupForInvite.id,
                    communityGroupInvites: selectedUsers.map(user => ({
                      receiverPatientId: user.isHealthPractitioner ? 0 : user.id,
                      receiverEmployeeId: user.isHealthPractitioner ? user.id : 0,
                      isHealthPractitioner: user.isHealthPractitioner
                    }))
                  };
                  
                  const response = await post('/communitygroupinvite', payload);
                  if (response?.statusCode === 200) {
                    notification({
                      type: 'success',
                      message: 'Invites sent successfully',
                      duration: 3000
                    });
                    getSentInvites();
                  }
                } catch (error) {
                  notification({
                    type: 'error',
                    message: 'Failed to send invites',
                    duration: 3000
                  });
                }
                setInviteModalOpen(false);
                setSelectedUsers([]);
                setSearchTerm('');
              }
            }}
            disabled={selectedUsers.length === 0}
          >
            Send Invites
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default GroupCreated;